import { useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import {
  Building2,
  BookOpen,
  Brain,
  CalendarCheck,
  ExternalLink,
  Eye,
  GraduationCap,
  HelpCircle,
  Images,
  Inbox,
  Instagram,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  MessageCircle,
  PanelsTopLeft,
  PhoneCall,
  Settings,
  Shield,
  Sparkles,
  Star,
  Stethoscope,
  Target,
  TrendingUp,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OverviewTab } from "./OverviewTab";
import { EnquiriesTab } from "./EnquiriesTab";
import { CollectionTab } from "./CollectionTab";
import { SettingsTab } from "./SettingsTab";
import { SectionsTab } from "./SectionsTab";
import { SeoTab } from "./SeoTab";
import { JournalTab } from "./JournalTab";
import { LeadsTab } from "./LeadsTab";
import { WhatsAppTab } from "./WhatsAppTab";
import { TeamTab } from "./TeamTab";
import { SeoAgentCard } from "./SeoAgentCard";
import { AdSpyTab } from "./AdSpyTab";
import { BrainTab } from "./BrainTab";
import { CallsTab } from "./CallsTab";
import { AgencyTab } from "./AgencyTab";
import { InstagramSync } from "@/components/InstagramSync";
import { SuperDashboard } from "./realestate/SuperDashboard";
import { ReOverviewTab } from "./realestate/ReOverviewTab";
import { ListingsTab } from "./realestate/ListingsTab";
import { AgentsTab } from "./realestate/AgentsTab";
import { ViewingsTab } from "./realestate/ViewingsTab";
import { BrokersTab } from "./realestate/BrokersTab";
import { ReSettingsTab } from "./realestate/ReSettingsTab";
import { ReBrainTab } from "./realestate/ReBrainTab";
import { ReTrainingTab } from "./realestate/ReTrainingTab";
import { ReEnquiriesTab } from "./realestate/ReEnquiriesTab";
import { ReLeadsTab } from "./realestate/ReLeadsTab";
import { ReChatsTab } from "./realestate/ReChatsTab";
import { ReCallsTab } from "./realestate/ReCallsTab";
import { ReSeoTab } from "./realestate/ReSeoTab";
import { ReSeoAgentTab } from "./realestate/ReSeoAgentTab";
import { ReAiTeamTab } from "./realestate/ReAiTeamTab";
import { ReAdSpyTab } from "./realestate/ReAdSpyTab";
import { ReInstagramTab } from "./realestate/ReInstagramTab";
import { ReContentTab, RE_CONTENT_PRESETS } from "./realestate/ReContentTab";

// Docstate (real-estate brand) backoffice navigation — mirrors the clinic's
// grouped structure, fully brand-scoped and isolated.
export const RE_NAV_GROUPS = [
  {
    group: "Overview",
    items: [{ id: "re-overview", label: "Dashboard", path: "/admin/re", icon: LayoutDashboard }],
  },
  {
    group: "Inbox & Sales",
    items: [
      { id: "re-enquiries", label: "Enquiries", path: "/admin/re/enquiries", icon: Inbox },
      { id: "re-leads", label: "Leads CRM", path: "/admin/re/leads", icon: Target },
      { id: "re-conversations", label: "Conversations", path: "/admin/re/conversations", icon: MessageCircle, staff: true },
      { id: "re-brain", label: "AI Brain", path: "/admin/re/brain", icon: Brain },
      { id: "re-training", label: "AI Training", path: "/admin/re/training", icon: GraduationCap },
      { id: "re-calls", label: "Calls", path: "/admin/re/calls", icon: PhoneCall },
    ],
  },
  {
    group: "Content",
    items: [
      { id: "re-insights", label: "Insights", path: "/admin/re/insights", icon: BookOpen },
      { id: "re-guides", label: "Area Guides", path: "/admin/re/guides", icon: PanelsTopLeft },
      { id: "re-gallery", label: "Gallery", path: "/admin/re/gallery", icon: Images },
      { id: "re-testimonials", label: "Testimonials", path: "/admin/re/testimonials", icon: Star },
      { id: "re-faqs", label: "FAQs", path: "/admin/re/faqs", icon: HelpCircle },
    ],
  },
  {
    group: "Listings & Team",
    items: [
      { id: "re-listings", label: "Listings", path: "/admin/re/listings", icon: Building2 },
      { id: "re-agents", label: "Agents", path: "/admin/re/agents", icon: UserCheck },
      { id: "re-viewings", label: "Viewings", path: "/admin/re/viewings", icon: CalendarCheck, staff: true },
      { id: "re-brokers", label: "Brokers", path: "/admin/re/brokers", icon: Users },
    ],
  },
  {
    group: "Marketing & SEO",
    items: [
      { id: "re-seo", label: "SEO Metadata", path: "/admin/re/seo", icon: TrendingUp },
      { id: "re-seo-agent", label: "SEO AI Agent", path: "/admin/re/seo-agent", icon: Sparkles },
      { id: "re-ai-team", label: "AI Team", path: "/admin/re/ai-team", icon: Users },
      { id: "re-ad-spy", label: "Ad Spy", path: "/admin/re/ad-spy", icon: Eye },
      { id: "re-instagram", label: "Instagram", path: "/admin/re/instagram", icon: Instagram },
    ],
  },
  {
    group: "System",
    items: [
      { id: "re-settings", label: "Brand Settings", path: "/admin/re/settings", icon: Settings, adminOnly: true },
    ],
  },
];


// Grouped subpage navigation — every item is a real, bookmarkable URL.
export const NAV_GROUPS = [
  {
    group: "Overview",
    items: [{ id: "overview", label: "Dashboard", path: "/admin/overview", icon: LayoutDashboard }],
  },
  {
    group: "Inbox & Patients",
    items: [
      { id: "enquiries", label: "Enquiries", path: "/admin/enquiries", icon: Inbox },
      { id: "leads", label: "Leads CRM", path: "/admin/leads", icon: Target },
      { id: "whatsapp", label: "WhatsApp Inbox", path: "/admin/whatsapp", icon: MessageCircle },
      { id: "brain", label: "AI Brain", path: "/admin/brain", icon: Brain },
      { id: "calls", label: "Calls", path: "/admin/calls", icon: PhoneCall },
    ],
  },
  {
    group: "Content",
    items: [
      { id: "journal", label: "Journal", path: "/admin/journal", icon: BookOpen },
      { id: "gallery", label: "Gallery", path: "/admin/gallery", icon: Images },
      { id: "testimonials", label: "Testimonials", path: "/admin/testimonials", icon: Star },
      { id: "faqs", label: "FAQs", path: "/admin/faqs", icon: HelpCircle },
    ],
  },
  {
    group: "Marketing & SEO",
    items: [
      { id: "seo", label: "SEO Metadata", path: "/admin/seo", icon: TrendingUp },
      { id: "seo-agent", label: "SEO AI Agent", path: "/admin/seo-agent", icon: Sparkles },
      { id: "ai-team", label: "AI Team", path: "/admin/ai-team", icon: Users },
      { id: "ad-spy", label: "Ad Spy", path: "/admin/ad-spy", icon: Eye },
      { id: "instagram", label: "Instagram", path: "/admin/instagram", icon: Instagram },
    ],
  },
  {
    group: "Clinic & Team",
    items: [
      { id: "treatments", label: "Treatments", path: "/admin/treatments", icon: Sparkles },
      { id: "doctors", label: "Doctors", path: "/admin/doctors", icon: UserCheck },
      { id: "team", label: "User Access", path: "/admin/team", icon: Shield, adminOnly: true },
    ],
  },
  {
    group: "System",
    items: [
      { id: "sections", label: "Homepage Sections", path: "/admin/sections", icon: PanelsTopLeft },
      { id: "settings", label: "Site Settings", path: "/admin/settings", icon: Settings, adminOnly: true },
    ],
  },
];

// Staff & doctors work in the WhatsApp inbox only; admins see everything.
const STAFF_PAGES = ["whatsapp"];

const NavItems = ({ groups, onNavigate, idSuffix = "" }) => (
  <>
    {groups.map((g) => (
      <div key={g.group}>
        <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#B38B2D]">{g.group}</p>
        <div className="mt-2 space-y-1">
          {g.items.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              end
              onClick={onNavigate}
              data-testid={`admin-nav-${item.id}${idSuffix}`}
              className={({ isActive }) =>
                `relative flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors duration-200 ${
                  isActive ? "bg-[#F8F3E6] font-semibold text-[#1A1615]" : "font-medium text-[#5A504B] hover:bg-[#F5EBE6]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#D4AF37] transition-opacity duration-200 ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                    aria-hidden="true"
                  />
                  <item.icon className={`h-4 w-4 ${isActive ? "text-[#B38B2D]" : "text-[#A39288]"}`} aria-hidden="true" />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </div>
    ))}
  </>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeBrand, setActiveBrand] = useState(() => localStorage.getItem("admin_brand") || "clinic");

  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" aria-label="Loading" />
      </div>
    );
  if (user === false) return <Navigate to="/admin/login" replace />;

  const isSuper = user.role === "super_admin";
  const isDocstateUser = user.brand === "docstate";
  const canDocstate = isSuper || isDocstateUser;
  const canClinic = isSuper || user.brand === "clinic" || user.brand === undefined;
  // super acts with full admin powers inside whichever brand they manage
  const isAdmin = user.role === "admin" || isSuper;
  // which brand's backoffice is on screen
  const brandMode = isDocstateUser ? "docstate" : isSuper ? activeBrand : "clinic";
  const isDocstate = brandMode === "docstate";

  const enterBrand = (slug) => {
    localStorage.setItem("admin_brand", slug);
    setActiveBrand(slug);
  };

  const clinicHome = isAdmin ? "/admin/overview" : "/admin/whatsapp";
  const docstateHome = isAdmin ? "/admin/re" : "/admin/re/viewings";
  const brandHome = isDocstate ? docstateHome : clinicHome;
  // super-admins land on the all-brands control; brand users land in their brand
  const home = isSuper ? "/admin/super" : isDocstateUser ? docstateHome : clinicHome;

  const clinicGroups = isAdmin
    ? NAV_GROUPS
    : NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => STAFF_PAGES.includes(i.id)) })).filter((g) => g.items.length);
  const docstateGroups = isAdmin
    ? RE_NAV_GROUPS
    : RE_NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => i.staff) })).filter((g) => g.items.length);
  const visibleGroups = isDocstate ? docstateGroups : clinicGroups;

  const allItems = visibleGroups.flatMap((g) => g.items);
  const current = allItems.find((i) => location.pathname === i.path || location.pathname.startsWith(i.path + "/"));
  const currentGroup = visibleGroups.find((g) => g.items.some((i) => i.id === current?.id));

  const allow = (id) => (isAdmin ? true : STAFF_PAGES.includes(id));
  const guard = (id, element) =>
    canClinic && allow(id) ? element : <Navigate to={brandHome} replace data-testid={`admin-guard-${id}`} />;
  const reGuard = (element, needAdmin) =>
    canDocstate && (!needAdmin || isAdmin) ? element : <Navigate to={brandHome} replace />;

  const brandName = isDocstate ? "Docstate Properties" : "Royal Skin";
  const roleLabel = isSuper ? "Super Admin" : isAdmin ? "Admin" : "Staff";

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-dashboard">
      {/* Top header — sticky, glass, shows exactly where you are */}
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-[#E8E2D9] bg-white/80 px-4 backdrop-blur-md lg:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#E8E2D9] text-[#5A504B] md:hidden"
            onClick={() => setDrawerOpen(true)}
            data-testid="admin-nav-toggle"
            aria-label="Open navigation"
          >
            <Menu className="h-4 w-4" aria-hidden="true" />
          </button>
          <img src="/assets/logo-mark.png" alt="" className="h-8 w-auto object-contain" />
          <div className="min-w-0 leading-none">
            <p className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[#B38B2D]" data-testid="admin-breadcrumb">
              Backoffice{currentGroup && currentGroup.group !== "Overview" ? ` · ${currentGroup.group}` : ""}
            </p>
            <h1 className="mt-1 truncate font-serif text-lg font-semibold text-[#1A1615]" data-testid="admin-page-title">
              {current?.label || "Dashboard"}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isSuper && (
            <div className="hidden items-center rounded-full border border-[#E8E2D9] bg-white p-0.5 md:flex" data-testid="brand-switcher">
              <Link
                to="/admin/super"
                data-testid="brand-switch-super"
                className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-[#5A504B] transition-colors hover:text-[#1A1615]"
              >
                All
              </Link>
              <button
                onClick={() => enterBrand("clinic")}
                data-testid="brand-switch-clinic"
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${!isDocstate ? "bg-[#1A1615] text-[#F3E5AB]" : "text-[#5A504B] hover:text-[#1A1615]"}`}
              >
                Clinic
              </button>
              <button
                onClick={() => enterBrand("docstate")}
                data-testid="brand-switch-docstate"
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${isDocstate ? "bg-[#1A1615] text-[#F3E5AB]" : "text-[#5A504B] hover:text-[#1A1615]"}`}
              >
                Docstate
              </button>
            </div>
          )}
          <span className="hidden text-xs text-[#91837C] lg:block" data-testid="admin-user-email">{user.email}</span>
          <span className="hidden rounded-full bg-[#F8F3E6] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a6d1f] sm:block" data-testid="admin-role-badge">
            {roleLabel}
          </span>
          <Link
            to="/"
            data-testid="admin-view-site-link"
            className="hidden items-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-xs font-medium text-[#1A1615] transition-colors hover:bg-[#D4AF37]/10 sm:inline-flex"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> View Site
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1615] px-4 py-2 text-xs font-medium text-[#F3E5AB] transition-opacity hover:opacity-90"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Logout
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar — grouped, fixed, no more horizontal tab scrolling */}
        <aside
          className="sticky top-16 hidden h-[calc(100vh-4rem)] w-64 shrink-0 flex-col gap-7 overflow-y-auto border-r border-[#E8E2D9] bg-[#FAF8F5]/90 px-4 py-7 backdrop-blur-xl md:flex"
          aria-label="Backoffice sections"
          data-testid="admin-sidebar"
        >
          <NavItems groups={visibleGroups} />
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="fixed inset-0 z-50 md:hidden" data-testid="admin-mobile-nav">
            <div className="absolute inset-0 bg-[#1A1615]/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
            <div className="absolute left-0 top-0 flex h-full w-72 flex-col gap-7 overflow-y-auto bg-[#FAF8F5] p-5 shadow-2xl">
              <div className="flex items-center justify-between">
                <span className="font-serif text-lg font-semibold text-[#1A1615]">{brandName}</span>
                <button
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#E8E2D9] text-[#5A504B]"
                  onClick={() => setDrawerOpen(false)}
                  data-testid="admin-nav-close"
                  aria-label="Close navigation"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <NavItems groups={visibleGroups} onNavigate={() => setDrawerOpen(false)} idSuffix="-mobile" />
            </div>
          </div>
        )}

        {/* Subpage canvas — generous spacing, no side-scrolling */}
        <main className="min-w-0 flex-1">
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
            <Routes>
              <Route index element={<Navigate to={home} replace />} />
              <Route path="super" element={isSuper ? <SuperDashboard onEnterBrand={enterBrand} /> : <Navigate to={brandHome} replace />} />
              <Route path="overview" element={guard("overview", <OverviewTab />)} />
              <Route path="enquiries" element={guard("enquiries", <EnquiriesTab />)} />
              <Route path="leads" element={guard("leads", <LeadsTab />)} />
              <Route path="whatsapp" element={<WhatsAppTab isAdmin={isAdmin} />} />
              <Route path="brain" element={guard("brain", <BrainTab />)} />
              <Route path="calls" element={guard("calls", <CallsTab />)} />
              <Route path="team" element={guard("team", <TeamTab />, true)} />
              <Route path="journal" element={guard("journal", <JournalTab />)} />
              <Route path="seo" element={guard("seo", <SeoTab />)} />
              <Route path="seo-agent" element={guard("seo-agent", <SeoAgentCard />)} />
              <Route path="ai-team" element={guard("ai-team", <AgencyTab />)} />
              <Route path="ad-spy" element={guard("ad-spy", <AdSpyTab />)} />
              <Route path="instagram" element={guard("instagram", (
                <div className="space-y-8">
                  <InstagramSync />
                  <CollectionTab collection="instagram_posts" />
                </div>
              ))} />
              <Route path="sections" element={guard("sections", <SectionsTab />)} />
              <Route path="settings" element={guard("settings", <SettingsTab />, true)} />
              {["treatments", "doctors", "gallery", "testimonials", "faqs"].map((c) => (
                <Route key={c} path={c} element={guard(c, <CollectionTab collection={c} />)} />
              ))}
              {/* Docstate (real-estate) backoffice */}
              <Route path="re" element={reGuard(<ReOverviewTab />, true)} />
              <Route path="re/enquiries" element={reGuard(<ReEnquiriesTab />, true)} />
              <Route path="re/leads" element={reGuard(<ReLeadsTab />, true)} />
              <Route path="re/conversations" element={reGuard(<ReChatsTab />, false)} />
              <Route path="re/brain" element={reGuard(<ReBrainTab />, true)} />
              <Route path="re/training" element={reGuard(<ReTrainingTab />, true)} />
              <Route path="re/calls" element={reGuard(<ReCallsTab />, true)} />
              <Route path="re/insights" element={reGuard(<ReContentTab {...RE_CONTENT_PRESETS.insights} />, true)} />
              <Route path="re/guides" element={reGuard(<ReContentTab {...RE_CONTENT_PRESETS.guides} />, true)} />
              <Route path="re/gallery" element={reGuard(<ReContentTab {...RE_CONTENT_PRESETS.gallery} />, true)} />
              <Route path="re/testimonials" element={reGuard(<ReContentTab {...RE_CONTENT_PRESETS.testimonials} />, true)} />
              <Route path="re/faqs" element={reGuard(<ReContentTab {...RE_CONTENT_PRESETS.faqs} />, true)} />
              <Route path="re/seo" element={reGuard(<ReSeoTab />, true)} />
              <Route path="re/seo-agent" element={reGuard(<ReSeoAgentTab />, true)} />
              <Route path="re/ai-team" element={reGuard(<ReAiTeamTab />, true)} />
              <Route path="re/ad-spy" element={reGuard(<ReAdSpyTab />, true)} />
              <Route path="re/instagram" element={reGuard(<ReInstagramTab />, true)} />
              <Route path="re/listings" element={reGuard(<ListingsTab />, true)} />
              <Route path="re/agents" element={reGuard(<AgentsTab />, true)} />
              <Route path="re/viewings" element={reGuard(<ViewingsTab />, false)} />
              <Route path="re/brokers" element={reGuard(<BrokersTab />, true)} />
              <Route path="re/settings" element={reGuard(<ReSettingsTab />, true)} />
              <Route path="*" element={<Navigate to={home} replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}
